// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';

export interface PIIConversation {
  id: number | string;
  username: string;
  pii_entities: string[];
  conversation: {
    user_query: string;
    bot: string;
  };
  timestamp: string;
  project_name?: string;
  scope?: string;
}

interface PIIDetailsPageProps {
  conversations: PIIConversation[];
  onBack: () => void;
  contextLabel?: string;
  loading?: boolean;
}

export default function PIIDetailsPage({ conversations, onBack, contextLabel, loading = false }: PIIDetailsPageProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reset to page 0 whenever conversations change (avoids out-of-range pagination)
  useEffect(() => {
    setPage(0);
  }, [conversations]);

  const paginatedConversations = conversations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDownload = () => {
    const headers = ['#', 'User', 'PII Entities', 'Query', 'Response', 'Project', 'Timestamp'];
    const rows = conversations.map((c, i) => [
      i + 1,
      c.username,
      `"${(c.pii_entities || []).join('; ').replace(/"/g, '""')}"`,
      `"${(c.conversation?.user_query || '').replace(/"/g, '""')}"`,
      `"${(c.conversation?.bot || '').replace(/"/g, '""')}"`,
      c.project_name || '',
      new Date(c.timestamp).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pii_conversations.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<Iconify icon="mdi:arrow-left" />} onClick={onBack} size="small">
          Back{contextLabel ? ` to ${contextLabel}` : ''}
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.error.main, 0.12),
            color: theme.palette.error.main,
          }}
        >
          <Iconify icon="mdi:shield-alert" width={28} />
        </Box>
        <Box>
          <Typography variant="h5">PII Queries</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {loading ? 'Loading conversations...' : `${conversations.length} conversations`}
          </Typography>
        </Box>
      </Stack>

      <Card>
        <CardHeader
          title="PII Warning"
          subheader={loading ? 'Loading...' : `${conversations.length} total conversations with PII detected`}
          action={
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownload} disabled={loading || conversations.length === 0}>
                <Iconify icon="mdi:download" />
              </IconButton>
            </Tooltip>
          }
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ px: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>PII Entities</TableCell>
                    <TableCell>Query</TableCell>
                    <TableCell>Response</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConversations.map((conv, index) => (
                    <TableRow key={conv.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{conv.username}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ maxWidth: 220 }}>
                          {(conv.pii_entities || [])
                            .flatMap((e) => e.split(','))
                            .map((entity, idx) => (
                              <Chip
                                key={idx}
                                label={entity.trim()}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem' }}
                              />
                            ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200, md: 250 } }}>
                          {conv.conversation?.user_query || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200, md: 250 } }}>
                          {conv.conversation?.bot || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {conv.project_name ? (
                          <Chip label={conv.project_name} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {new Date(conv.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={conversations.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
